import { demographicDimension } from '@digital-twins/shared-types'
import type { Speaker } from '@digital-twins/shared-types'
import { formatGenderForBlurb } from '@/lib/utils/twin-blurb'

export interface RespondentTextProfile {
  /** The twin's own tagline, stored on the profile: never a report label (D16). */
  tagline?: string
  /** The report's speaker record (`speakers`, D16). */
  speaker?: Speaker | null
  summary?: string
  respondent_attributes?: Record<string, unknown>
}

export interface StudyChatProfile extends RespondentTextProfile {
  respondent_id?: string
  name?: string
  image_url?: string
}

/**
 * The role line under a report respondent: the speaker label the analysis
 * wrote (D16). A profile stored before it has none and shows the name only;
 * never a twin tagline or a source-panel attribute.
 */
export function getRespondentRole(profile: RespondentTextProfile): string | undefined {
  return profile.speaker?.label?.trim() || undefined
}

export function getRespondentDescription(profile: RespondentTextProfile): string | undefined {
  if (profile.summary) return profile.summary
  const attrs = profile.respondent_attributes ?? {}
  // Same fallback chain as AudienceDetailContent personas.
  for (const key of ['persona_summary', 'description', 'Description', 'bio', 'Bio']) {
    const val = attrs[key]
    if (typeof val === 'string' && val.trim()) return val.trim()
  }
  const parts: Array<string> = []
  const gender = attrs.gender ?? attrs.Gender
  const age = demographicDimension('age').keys.map((key) => attrs[key]).find((value) => value != null && value !== '')
  const city = attrs.city ?? attrs.City
  const country = attrs.country ?? attrs.Country
  const industry = attrs.industry ?? attrs.Industry
  const company = attrs.company ?? attrs.Company
  if (gender && typeof gender === 'string') {
    const genderLabel = formatGenderForBlurb(gender)
    if (genderLabel) parts.push(genderLabel)
  }
  if (age) parts.push(`${age} years old`)
  if (city && typeof city === 'string') parts.push(city)
  else if (country && typeof country === 'string') parts.push(country)
  if (industry && typeof industry === 'string') parts.push(industry)
  else if (company && typeof company === 'string') parts.push(company)
  return parts.length > 0 ? parts.join(' · ') : undefined
}
